import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";
import { ReceiveReceiptResponseAction } from "./SendReceipt";
import {
  PohodaClient,
  BatchNumberClient,
  IssuedOrder,
  ReceivedOrderProduct,
  SkuWeightKind,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface IssuedOrderState {
  isLoading: boolean;
  error: string;
  issuedOrder: IssuedOrder | null;
  receivedItems: ReceivedOrderProduct[];
  selectedProduct: ReceivedOrderProduct | null;
  intakeCount: number;
  printCount: number;
  isLoadingBatchNumber: boolean;
  loadingBatchNumberError: string;
  missingGoodsWillBeDeliveredLater: boolean;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface LoadIssuedOrderAction {
  type: "LOAD_ISSUED_ORDER";
  orderNumber: string;
}

interface ReceiveIssuedOrderAction {
  type: "RECEIVE_ISSUED_ORDER";
  orderNumber: string;
  issuedOrder: IssuedOrder;
}

interface CancelLoadIssuedOrderAction {
  type: "CANCEL_LOAD_ISSUED_ORDER";
  reason: string;
}

interface ChangeSelectedProductAction {
  type: "CHANGE_SELECTED_PRODUCT";
  selectedProductCode: string;
}

interface ChangePrintCountAction {
  type: "CHANGE_PRINT_COUNT";
  count: number;
}

interface ChangeIntakeCountAction {
  type: "CHANGE_INTAKE_COUNT";
  count: number;
}

interface RequestBatchNumberAction {
  type: "REQUEST_BATCH_NUMBER";
  product: ReceivedOrderProduct;
  intakeCount: number;
}

interface ReceiveBatchNumberAction {
  type: "RECEIVE_BATCH_NUMBER";
  product: ReceivedOrderProduct;
  intakeCount: number;
  batchNumber: string;
}

interface CancelRequestBactchNumberAction {
  type: "CANCEL_REQUEST_BATCH_NUMBER";
  reason: string;
}

interface RemoveIntakeEntryAction {
  type: "REMOVE_INTAKE_ENTRY";
  product: ReceivedOrderProduct;
  intakeEntryId: number;
}

interface SetMissingGoodsWillBeDeliveredLaterAction {
  type: "SET_MISSING_GOODS_WILL_BE_DELIVERED_LATER";
  value: boolean;
}

interface UpdateProductWeightAction {
  type: "UPDATE_PRODUCT_WEIGHT";
  stockItemId: number;
  weightG: number | null;
  weightKind: SkuWeightKind;
}

interface RestoreFromLocalStorageAction {
  type: "RESTORE_FROM_LOCALSTORAGE";
  receivedItems: ReceivedOrderProduct[];
  missingGoodsWillBeDeliveredLater: boolean;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | LoadIssuedOrderAction
  | ReceiveIssuedOrderAction
  | CancelLoadIssuedOrderAction
  | ChangeSelectedProductAction
  | ChangePrintCountAction
  | ChangeIntakeCountAction
  | RequestBatchNumberAction
  | ReceiveBatchNumberAction
  | CancelRequestBactchNumberAction
  | RemoveIntakeEntryAction
  | SetMissingGoodsWillBeDeliveredLaterAction
  | UpdateProductWeightAction
  | RestoreFromLocalStorageAction
  | ReceiveReceiptResponseAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

// Initialize the API clients
const pohodaClient = new PohodaClient(new ApiConfig());
const batchNumberClient = new BatchNumberClient(new ApiConfig());

export const actionCreators = {
  requestIssuedOrder:
    (orderNumber: string): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      pohodaClient
        .loadIssuedOrder(orderNumber)
        .then((data) => {
          dispatch({
            type: "RECEIVE_ISSUED_ORDER",
            orderNumber,
            issuedOrder: data,
          });
        })
        .catch((error: ApiError) =>
          dispatch({
            type: "CANCEL_LOAD_ISSUED_ORDER",
            reason: getApiErrorMessage(error),
          })
        );

      dispatch({ type: "LOAD_ISSUED_ORDER", orderNumber });
    },
  changeSelectedProduct: (productCode: string) =>
    ({
      type: "CHANGE_SELECTED_PRODUCT",
      selectedProductCode: productCode,
    } as ChangeSelectedProductAction),
  changePrintCount: (count: number) =>
    ({
      type: "CHANGE_PRINT_COUNT",
      count,
    } as ChangePrintCountAction),
  changeIntakeCount: (count: number) =>
    ({
      type: "CHANGE_INTAKE_COUNT",
      count,
    } as ChangeIntakeCountAction),
  //addIntakeEntry: (intakeCount: number, product: Product) => ({
  //    type: 'ADD_INTAKE_ENTRY',
  //    product,
  //    intakeEntry: {
  //        QuantityReceived: intakeCount,
  //    },
  //} as RequestBatchNumberAction),
  addIntakeEntry:
    (
      intakeCount: number,
      product: ReceivedOrderProduct
    ): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      if (product.IsBatch) {
        batchNumberClient
          .assignNumber()
          .then((response) => {
            dispatch({
              type: "RECEIVE_BATCH_NUMBER",
              product,
              intakeCount,
              batchNumber: response.BatchNumber,
            });
          })
          .catch((error: ApiError) =>
            dispatch({
              type: "CANCEL_REQUEST_BATCH_NUMBER",
              reason: getApiErrorMessage(error),
            })
          );

        dispatch({ type: "REQUEST_BATCH_NUMBER", product, intakeCount });
      } else {
        dispatch({
          type: "RECEIVE_BATCH_NUMBER",
          product,
          intakeCount,
          batchNumber: "",
        });
      }
    },
  removeIntakeEntry: (product: ReceivedOrderProduct, intakeEntryId: number) =>
    ({
      type: "REMOVE_INTAKE_ENTRY",
      product,
      intakeEntryId,
    } as RemoveIntakeEntryAction),
  setMissingGoodsWillBeDeliveredLater: (value: boolean) =>
    ({
      type: "SET_MISSING_GOODS_WILL_BE_DELIVERED_LATER",
      value,
    } as SetMissingGoodsWillBeDeliveredLaterAction),
  updateProductWeight: (stockItemId: number, weightG: number | null, weightKind: SkuWeightKind) =>
    ({
      type: "UPDATE_PRODUCT_WEIGHT",
      stockItemId,
      weightG,
      weightKind,
    } as UpdateProductWeightAction),
  restoreFromLocalStorage: (receivedItems: ReceivedOrderProduct[], missingGoodsWillBeDeliveredLater: boolean) =>
    ({
      type: "RESTORE_FROM_LOCALSTORAGE",
      receivedItems,
      missingGoodsWillBeDeliveredLater,
    } as RestoreFromLocalStorageAction),
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: IssuedOrderState = {
  isLoading: false,
  error: "",
  issuedOrder: null,
  receivedItems: [],
  selectedProduct: null,
  intakeCount: 0,
  printCount: 0,
  isLoadingBatchNumber: false,
  loadingBatchNumberError: "",
  missingGoodsWillBeDeliveredLater: false,
};

export const reducer: Reducer<IssuedOrderState> = (
  state: IssuedOrderState | undefined,
  incomingAction: Action
): IssuedOrderState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case "LOAD_ISSUED_ORDER":
      if (action.orderNumber == state.issuedOrder?.Number)
        return {
          ...state,
          error: "",
          isLoading: true,
        };
      else
        return {
          ...state,
          error: "",
          isLoading: true,
          issuedOrder: null,
          intakeCount: 0,
          receivedItems: [],
          selectedProduct: null,
          missingGoodsWillBeDeliveredLater: false,
        };
    case "RECEIVE_ISSUED_ORDER":
      return {
        ...state,
        error: "",
        isLoading: false,
        issuedOrder: action.issuedOrder,
        receivedItems: action.issuedOrder.Items.map(
          (item): ReceivedOrderProduct => ({
            ...item,
            IntakeEntries: [],
          })
        ),
        missingGoodsWillBeDeliveredLater: false,
      };
    case "CANCEL_LOAD_ISSUED_ORDER":
      return {
        ...state,
        isLoading: false,
        error: action.reason,
      };
    case "CHANGE_SELECTED_PRODUCT":
      const matchingProduct = state.receivedItems?.find(
        (value) => value.Code == action.selectedProductCode
      );
      if (matchingProduct)
        return {
          ...state,
          selectedProduct: { ...matchingProduct },
          intakeCount: 0,
        };
      else return state;
    case "CHANGE_PRINT_COUNT":
      return {
        ...state,
        printCount: action.count > 0 ? action.count : 0,
      };
    case "CHANGE_INTAKE_COUNT":
      return {
        ...state,
        intakeCount: action.count > 0 ? action.count : 0,
      };
    case "REQUEST_BATCH_NUMBER":
      return {
        ...state,
        isLoadingBatchNumber: true,
        loadingBatchNumberError: "",
      };
    case "RECEIVE_BATCH_NUMBER":
      if (action.intakeCount > 0) {
        let receivedItemsCopy = [...state.receivedItems];
        const productIndex = receivedItemsCopy.findIndex(
          (product) => product.StockItemId == action.product.StockItemId
        );

        if (productIndex == -1) {
          receivedItemsCopy.push({
            ...action.product,
            IntakeEntries: [
              {
                Id: 0,
                QuantityReceived: action.intakeCount,
                BatchNumber: action.batchNumber,
              },
            ],
          });
        } else {
          receivedItemsCopy[productIndex] = {
            ...receivedItemsCopy[productIndex],
            IntakeEntries: [
              ...receivedItemsCopy[productIndex].IntakeEntries,
              {
                Id: receivedItemsCopy[productIndex].IntakeEntries.length,
                QuantityReceived: action.intakeCount,
                BatchNumber: action.batchNumber,
              },
            ],
          };
        }

        return {
          ...state,
          receivedItems: receivedItemsCopy,
          isLoadingBatchNumber: false,
          loadingBatchNumberError: "",
        };
      } else {
        return state;
      }
    case "CANCEL_REQUEST_BATCH_NUMBER":
      return {
        ...state,
        isLoadingBatchNumber: false,
        loadingBatchNumberError: action.reason,
      };
    case "REMOVE_INTAKE_ENTRY":
      let itemIndex = state.receivedItems.findIndex(
        (p) => p.StockItemId == action.product.StockItemId
      );
      let newIntakeEntries = state.receivedItems[
        itemIndex
      ].IntakeEntries.filter((e) => e.Id != action.intakeEntryId);

      return {
        ...state,
        receivedItems: [
          ...state.receivedItems.slice(0, itemIndex),
          {
            ...state.receivedItems[itemIndex],
            IntakeEntries: newIntakeEntries,
          },
          ...state.receivedItems.slice(itemIndex + 1),
        ],
      };
    case "SET_MISSING_GOODS_WILL_BE_DELIVERED_LATER":
      return {
        ...state,
        missingGoodsWillBeDeliveredLater: action.value,
      };
    case "UPDATE_PRODUCT_WEIGHT":
      return {
        ...state,
        receivedItems: state.receivedItems.map(item =>
          item.StockItemId === action.stockItemId
            ? { ...item, WeightG: action.weightG, WeightKind: action.weightKind }
            : item
        ),
      };
    case "RESTORE_FROM_LOCALSTORAGE":
      return {
        ...state,
        receivedItems: action.receivedItems,
        missingGoodsWillBeDeliveredLater: action.missingGoodsWillBeDeliveredLater,
      };
    case "RECEIVE_RECEIPT_RESPONSE":
      return {
        ...state,
        issuedOrder: null,
        receivedItems: [],
        missingGoodsWillBeDeliveredLater: false,
      };
    default:
      return state;
  }
};
